import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';
import LanguageSwitcher from '@/components/LanguageSwitcher';

export default function Terms() {
  const navigate = useNavigate();
  const { t } = useLanguage();

  return (
    <div className="min-h-screen py-8 px-6">
      <div className="absolute top-6 right-6 z-20">
        <LanguageSwitcher />
      </div>

      <div className="max-w-4xl mx-auto">
        <button
          data-testid="back-btn"
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-pink-600 mb-6"
          style={{ transition: 'color 0.2s' }}
        >
          <ArrowLeft className="w-5 h-5" />
          {t('common.back')}
        </button>

        <div className="glass-card p-8">
          <div className="flex items-center gap-3 mb-6">
            <FileText className="w-8 h-8 text-pink-500" />
            <h1 className="text-3xl font-bold gradient-text">{t('terms.title')}</h1>
          </div>

          <p className="text-sm text-gray-500 mb-8">{t('terms.lastUpdated')}</p>

          <div className="space-y-6 text-gray-700">
            <section>
              <h2 className="text-xl font-bold text-gray-800 mb-3">{t('terms.objectAndAcceptance.title')}</h2>
              <p className="whitespace-pre-line">{t('terms.objectAndAcceptance.content')}</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-800 mb-3">{t('terms.accessConditions.title')}</h2>
              <p className="mb-2">{t('terms.accessConditions.ageMinimum')}</p>
              <p className="mb-2">{t('terms.accessConditions.legalCapacity')}</p>
              <p className="mb-2">{t('terms.accessConditions.lawCompliance')}</p>
              <p className="font-semibold mb-2 mt-4">{t('terms.accessConditions.payment')}</p>
              <ul className="list-none pl-0 space-y-1">
                <li>{t('terms.accessConditions.free500')}</li>
                <li>{t('terms.accessConditions.after500')}</li>
                <li>{t('terms.accessConditions.donations')}</li>
              </ul>
              <p className="mt-4">{t('terms.accessConditions.truthfulness')}</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-800 mb-3">{t('terms.dataProtection.title')}</h2>
              <p className="mb-2">{t('terms.dataProtection.commitment')}</p>
              <p className="mb-2">{t('terms.dataProtection.rights')}</p>
              <p>{t('terms.dataProtection.deletion')}</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-800 mb-3">{t('terms.serviceUsage.title')}</h2>
              <p className="mb-2">{t('terms.serviceUsage.intro')}</p>
              <ul className="list-none pl-0 space-y-1">
                <li>{t('terms.serviceUsage.noHarassment')}</li>
                <li>{t('terms.serviceUsage.noOffensive')}</li>
                <li>{t('terms.serviceUsage.noFalse')}</li>
                <li>{t('terms.serviceUsage.noSpam')}</li>
                <li>{t('terms.serviceUsage.noIllegal')}</li>
                <li>{t('terms.serviceUsage.noCommercial')}</li>
                <li>{t('terms.serviceUsage.exclusion')}</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-800 mb-3">{t('terms.userContent.title')}</h2>
              <p>{t('terms.userContent.ownership')}</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-800 mb-3">{t('terms.artificialIntelligence.title')}</h2>
              <p className="mb-2">{t('terms.artificialIntelligence.usage')}</p>
              <p>{t('terms.artificialIntelligence.disclaimer')}</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-800 mb-3">{t('terms.liabilityLimitation.title')}</h2>
              <p className="mb-2">{t('terms.liabilityLimitation.asIs')}</p>
              <p>{t('terms.liabilityLimitation.safety')}</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-800 mb-3">{t('terms.termination.title')}</h2>
              <p className="mb-2">{t('terms.termination.userRight')}</p>
              <p>{t('terms.termination.serviceRight')}</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-800 mb-3">{t('terms.modifications.title')}</h2>
              <p>{t('terms.modifications.content')}</p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-800 mb-3">{t('terms.contact.title')}</h2>
              <p>{t('terms.contact.content')}</p>
            </section>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={() => navigate('/auth?mode=signup')}
              className="btn-primary w-full py-4"
            >
              {t('terms.acceptAndContinue')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
